<template>
    <div class="appt-setup">

      <div class="">

        <p v-if="!appointment || !encounter">No appointment or encounter found, please go back and select one.</p>
        
        <div v-if="encounter">
          <h5><strong>Setup Tasks</strong> <small class="light-weight">required</small></h5>

          <label class="normal-weight form-check">
            <input type="checkbox" name="personal" v-model="setupTasks.personal" value="setupTasks.personal" class="form-check-input">
            <span>
              Make at least 2 connections with the patient
              <i class="far fa-question-circle fa-tooltip"><span class="tip">
                  <strong>Examples:</strong><br />
                  What did you do last weekend?<br />
                  How's your day going so far?<br />
                  Any new TV show reccs?<br />
                  How have you been feeling?<br />
                </span></i>
            </span>
          </label>

          <!-- <div class="row">
            <div class="col">
              <label class="normal-weight form-check">
                <input type="checkbox" name="pmtCollected" v-model="setupTasks.pmtCollected" value="setupTasks.pmtCollected" class="form-check-input">
                <span>Payment Collected:</span>
              </label>
              <input v-model.number="patNum" name="patNum" class="form-control" placeholder="Enter Patient Number" type="number" @keyup.enter="loadPlansByPatNum"/>
            </div>
            <div class="col">
              Due Today: {{ pmtDueToday }}
            </div>
          </div> -->
          
          <label class="normal-weight form-check">
            <input type="checkbox" name="consent" v-model="setupTasks.consent" value="setupTasks.consent" class="form-check-input">
            <span>Consent / Paperwork Signed</span>
          </label>

          <label class="normal-weight form-check">
            <input type="checkbox" name="bp" v-model="setupTasks.bp" value="setupTasks.bp" class="form-check-input">
            <span>Blood Pressure Taken</span>
          </label>

          <label class="normal-weight form-check">
            <input type="checkbox" name="hx" v-model="setupTasks.hx" value="setupTasks.hx" class="form-check-input">
            <span>Med History Reviewed</span>
          </label>

          <label class="normal-weight form-check">
            <input type="checkbox" name="imagesDisplayed" v-model="setupTasks.imagesDisplayed" value="setupTasks.imagesDisplayed" class="form-check-input">
            <span>Images Displayed in Room</span>
          </label>
        </div>

        <hr class="hr-dark-color" />

        <div v-if="encounter && appointment">
          <h5><strong>Select Providers</strong></h5>
          <p class="loading" v-if="loadingProviders"><i class="far fa-fw fa-pulse fa-spinner"></i> loading providers...</p>
          <p v-if="!loadingProviders && !providers"><em>no providers found in Open Dental</em></p>
          <div v-if="providers" class="form-group">
            <label for="primaryProvider">Primary Provider <small class="light-weight">required</small></label>
            <select id="primaryProvider" v-model="primaryProvider" class="form-control">
              <option disabled :value="null">select one</option>
              <option v-for="prov in providers" :value="prov.ProvNum">{{ prov.Abbr }} - {{ prov.LName }}, {{ prov.FName }}</option>
            </select>
          </div>
          <div v-if="providers" class="form-group">
            <label for="secondaryProvider">Secondary Provider <small class="light-weight">optional</small></label>
            <select id="secondaryProvider" v-model="secondaryProvider" class="form-control">
              <option disabled :value="null">select one</option>
              <option v-for="prov in providers" :value="prov.ProvNum">{{ prov.Abbr }} - {{ prov.LName }}, {{ prov.FName }}</option>
            </select>
          </div>
          <div class="form-group">
            <label for="assistant">Assistant <small class="light-weight">optional</small></label>
            <p class="loading" v-if="loadingAssistants"><i class="far fa-fw fa-pulse fa-spinner"></i> loading assistants...</p>
            <p v-if="!loadingAssistants && !assistants"><em>no assistants found in Open Dental</em></p>
            <select v-if="assistants" id="assistant" v-model="assistant" class="form-control">
              <option disabled :value="null">select one</option>
              <option v-for="asst in assistants" :value="asst.UserNum">{{ asst.UserName }}</option>
            </select>
          </div>
        </div>

        <hr class="hr-dark-color" />

        <p v-if="!passedValidation" class="validation-warning text-center"><em>All tasks are required in order to begin the appointment.</em></p>

        <p v-if="passedValidation" class="box-ready"><i class="fas fa-check color-green"></i> <strong>Ready!</strong> Nice work! Ready to begin the appointment.</p>
        <p v-if="passedValidation" class="text-center"><em>Remember: <strong>Bridge the connection to build understanding!</strong></em></p>

        <div class="row-actions-footer text-center">
          <button v-if="!savingProgress" @click.prevent="beginEncounter" class="btn btn-green btn-lg">Save &amp; Next<i class="fas fa-angle-right fa-fw"></i></button>
          <p v-if="savingProgress" class="loading"><i class="far fa-fw fa-pulse fa-spinner"></i> saving progress</p>
        </div>

      </div> <!-- /.col -->
    
    </div> <!-- /.row -->
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import LayoutPrivate from '../components/LayoutPrivate.vue'
import ListErrors from '../components/ListErrors.vue'
import moment from 'moment-timezone';

export default {
  components: {
    LayoutPrivate, ListErrors,
  },
  data() {
    return {
      setupTasks: {
        personal: false,
        // pmtCollected: null,
        consent: false,
        bp: false,
        hx: false,
        imagesDisplayed: false,
      },
      // pmtDueToday: null,
      providers: null,
      assistants: null,
      primaryProvider: null,
      secondaryProvider: null,
      assistant: null,
      loadingProviders: false,
      loadingAssistants: false,
      savedProgress: false,
      savingProgress: false
    };
  },
  computed: {
    ...mapGetters(['user', 'user_props', 'custom_claims', 'appointment', 'encounter']),
    passedValidation() {
      // validate all tasks complete
        // secondary provider & assistant are optional
      if(
        Object.values(this.setupTasks).every(task => task === true)
        && this.primaryProvider
        // && this.assistant
      ) {
        return true;
      }
      return false;
    },
  },
  watch: {
    custom_claims(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadProviders();
        this.loadAssistants();
      }
    },
    encounter(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.prefillEncounterSetup();
      }
    },
  },
  methods: {
    prefillEncounterSetup() {
      // prefill setup tasks from encounter if available
      if(this.encounter && this.encounter.setupTasks) {
        this.setupTasks = {
          ...this.setupTasks,
          ...this.encounter.setupTasks,
        };
      }
      // prefill selected providers from encounter if available
      if(this.encounter) {
        this.primaryProvider = this.encounter.primaryProvider || null;
        this.secondaryProvider = this.encounter.secondaryProvider || null;
        this.assistant = this.encounter.assistant || null;
      }
    },
    async loadProviders() {
      // console.log('loading providers');
      const uid = this.user?.uid;
      const customerId = this.custom_claims?.customerId;
      const locationId = this.custom_claims?.authorized_location;
      if (!uid || !customerId || !locationId) {
        // Required parameters missing, exit early
        console.log('missing parameters for loadProviders');
        return;
      }
      this.loadingProviders = true;
      try {
        const loadProviders = Vue.functions.httpsCallable('loadProviders');
        const payload = { uid, customerId, locationId };
        const result = await loadProviders(payload);
        if(result.data && Array.isArray(result.data) && result.data.length) {
          // console.log('loaded providers count:',result.data.length);
          this.providers = result.data.sort((a,b) => (a.Abbr > b.Abbr) ? 1 : ((b.Abbr > a.Abbr) ? -1 : 0));
        } else {
          console.log('no providers found');
        }
      } catch (err) {
        console.error('Failed to load providers:', err);
        this.providers = null;
      }
      this.loadingProviders = false;
    },
    async loadAssistants() {
      // console.log('loading assistants');
      const uid = this.user?.uid;
      const customerId = this.custom_claims?.customerId;
      const locationId = this.custom_claims?.authorized_location;
      if (!uid || !customerId || !locationId) {
        // Required parameters missing, exit early
        console.log('missing parameters for loadAssistants');
        return;
      }
      this.loadingAssistants = true;
      try {
        const loadAssistants = Vue.functions.httpsCallable('loadAssistants');
        const payload = { uid, customerId, locationId };
        const result = await loadAssistants(payload);
        if(result.data && Array.isArray(result.data) && result.data.length) {
          this.assistants = result.data.sort((a,b) => (a.UserName > b.UserName) ? 1 : ((b.UserName > a.UserName) ? -1 : 0));
        } else {
          console.log('no assistants found');
        }
      } catch (err) {
        console.error('Failed to load assistants:', err);
        this.providers = null;
      }
      this.loadingAssistants = false;
    },
    async beginEncounter() {
      // exit if missing data
      if(!this.custom_claims || !this.custom_claims.customerId || !this.custom_claims.authorized_location || !this.encounter) {
        console.log('missing data to update appointment encounters');
        return;
      }

      // construct encounter object
      const encounterObject = {
        ...this.encounter,
        setupTasks: this.setupTasks,
        primaryProvider: this.primaryProvider,
        secondaryProvider: this.secondaryProvider,
        assistant: this.assistant,
        // allSetupTasksCompleted: true,
        // encounter_setup_status: 'complete',
        customerId: this.custom_claims.customerId,
        locationId: this.custom_claims.authorized_location,
      };

      // if at least some checkboxes or fields complete, update status
      if(
        (Object.values(this.setupTasks).some(task => task === true)
        || this.primaryProvider
        || this.secondaryProvider
        || this.assistant)
        && !this.passedValidation
      ) {
        encounterObject.encounter_setup_status = 'inProgress';
        // overwrite in case something is un-checked
        encounterObject.allSetupTasksCompleted = false;
      }
      // if all passed, set status to completee
      if(this.passedValidation) {
        encounterObject.encounter_setup_status = 'complete';
        encounterObject.allSetupTasksCompleted = true;
      }
      
      // save encounter data in store
      this.$store.dispatch('updateEncounter',encounterObject);
      // save patient number in store
      this.$store.dispatch('updatePatNum',this.appointment.PatNum);
      // save patient name in store
      this.$store.dispatch('updatePatName',this.appointment.PatientName);
      // update in firestore
      let encounterRef = Vue.firestore.doc(`/customers/${this.custom_claims.customerId}/locations/${this.custom_claims.authorized_location}/encounters/${encounterObject.encounter_id}`);
      try {
        await encounterRef.update(encounterObject);
        // console.log('encounter updated successfully');
        this.savedProgress = true;
      } catch (err) {
        console.error('Failed to update encounter during save progress:', err);
        return;
      }
      // navigate to encounter page
      this.$router.push({ name: 'appointmentTreatSummary' });
    },
  },
  mounted() {
    if (this.custom_claims) {
      this.loadProviders();
      this.loadAssistants();
    }
    if (this.encounter) {
      this.prefillEncounterSetup();
    }
  },
  
};
</script>

<style lang="less" scoped>
@import "../assets/less/variables.less";

.appt-setup {
  padding-bottom: 30px;

  .hr-dark-color {
    margin: 30px 0
  }
}
.hr-salmon {
  margin-top: 0;
}
.btn-salmon:disabled {
  color: @light-color !important;
  opacity: 1 !important;
  cursor: not-allowed !important;
}
.btn-salmon:disabled:hover {
  background-color: @color-salmon !important;
  color: @light-color !important;
}
p.validation-warning {
  margin-bottom: 30px;
}
.row-actions-footer {
  margin: 0;
}
.box-ready {
  text-align: center;
  padding: 10px;
  border: 1px solid @color-green;
}
p.savedProgress {
  margin-top: 10px
}

</style>
